'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import translations from '../i18n';

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = ['zh-TW', 'en', 'ja'];
const DEFAULT_LANGUAGE = 'zh-TW';

function getInitialLanguage() {
  try {
    const saved = localStorage.getItem('language');
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
      return saved;
    }
  } catch (e) {
    // localStorage unavailable
  }
  return DEFAULT_LANGUAGE;
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = useCallback((lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
      try {
        localStorage.setItem('language', lang);
      } catch (e) {
        // localStorage unavailable
      }
    }
  }, []);

  const t = useCallback(
    (key) => {
      const keys = key.split('.');
      let value = translations[language];
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // Fallback to zh-TW
          let fallback = translations[DEFAULT_LANGUAGE];
          for (const fk of keys) {
            if (fallback && typeof fallback === 'object' && fk in fallback) {
              fallback = fallback[fk];
            } else {
              return key; // Return key if not found anywhere
            }
          }
          return typeof fallback === 'string' ? fallback : key;
        }
      }
      return typeof value === 'string' ? value : key;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
